import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { InputField, SelectField } from '@/components/shared/form'
import type { Platform } from '@/models'

const PLATFORM_OPTIONS = [
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'both', label: 'Both' },
]

export interface ReleaseValues {
  version: string
  platform: 'ios' | 'android' | 'both'
}

interface ReleaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  platform: Platform
  onCreate: (values: ReleaseValues) => void
  /* Server-side validation errors from a failed submit; surfaced in the form
   * so the dialog can stay open for the user to correct and retry. */
  serverErrors?: Record<string, string>
  /* Latest existing version per platform, shown as a hint so the user knows
   * what the next version should bump from. */
  latestByPlatform?: Partial<Record<Platform, string>>
}

/* A release is created empty and then configured on the page it lands on —
 * policy flags, config bundle, metadata. Asking for any of that up front makes
 * a dialog you have to fill in before you can see what you are filling in. */
export function ReleaseDialog({
  open,
  onOpenChange,
  platform,
  onCreate,
  serverErrors,
  latestByPlatform,
}: ReleaseDialogProps) {
  const form = useForm<ReleaseValues>({ version: '', platform })

  useEffect(() => {
    if (open) {
      form.setData({ version: '', platform })
      form.clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, platform])

  // A failed submit keeps the dialog open; map the server errors onto the form.
  useEffect(() => {
    if (serverErrors && Object.keys(serverErrors).length > 0) {
      form.setError(serverErrors as Record<keyof ReleaseValues, string>)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverErrors])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.clearErrors()

    if (!/^\d+\.\d+\.\d+$/.test(form.data.version)) {
      form.setError('version', 'Use semantic versioning, e.g. 4.2.0.')
      return
    }

    onCreate({ ...form.data })
  }

  const latest = latestByPlatform ?? {}
  const fmtLatest = (p: Platform) =>
    `${p === 'ios' ? 'iOS' : 'Android'} ${latest[p] ?? '— none yet'}`
  const latestHint =
    form.data.platform === 'both'
      ? `Latest: ${fmtLatest('ios')} · ${fmtLatest('android')}`
      : `Latest ${fmtLatest(form.data.platform)}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="w-[460px]">
      <DialogHeader>
        <DialogTitle>New release</DialogTitle>
      </DialogHeader>

      <DialogBody>
        <form id="release-form" onSubmit={submit} noValidate className="flex flex-col gap-3">
          <div className="grid grid-cols-[1fr_140px] gap-2">
            <InputField
              form={form}
              name="version"
              label="Version"
              placeholder="4.2.0"
              required
              className="tabular-nums"
            />
            <SelectField form={form} name="platform" options={PLATFORM_OPTIONS} label="Platform" />
          </div>
          <p className="text-fg-subtle text-xs tabular-nums">{latestHint}</p>
        </form>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button type="submit" form="release-form">
          Create Release
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
